import {
  TEXT_BODY_MAX_LENGTH,
  clampTextBodyAtCaret,
  estimateEmWidth,
  type TextBodyClampReason,
} from "@refinement-canvas/domain";
import { TEXT_DEFAULT_W, textInitialHeight } from "../features/canvas/object-placement-defaults";
import { STICKY_LINE_HEIGHT_RATIO } from "./sticky-font-fit";

/** 貼り付けたテキスト図形の初期サイズ上限（ステージ座標 px）。 */
export const PASTE_TEXT_MAX_W = 480;
export const PASTE_TEXT_MAX_H = 480;

/** 現在のテキスト表示には水平方向の padding が無いため、幅へ余白を加えない。 */
const PASTE_TEXT_PADDING_X_PX = 0;

/**
 * 貼り付けるテキスト図形の初期サイズを、保存する本文から決定する。
 * Markdown の表示結果ではなく raw 本文から決定的に見積もる設計であり、表示との差異は許容する。
 * この値は利用者がリサイズするまでの初期値で、本文が枠へ収まらない状態も許容される。
 * 引数展開を使わず走査することで、改行が多い本文でも安全に計算する。
 */
export function resolvePastedTextFrameSize(params: {
  readonly text: string;
  readonly fontPx: number;
}): { readonly w: number; readonly h: number } {
  const lines = params.text.split("\n");
  // 大量の行を引数展開せず、最長行の推定幅を 1 回の走査で求める。
  const maxEm = lines.reduce((max, line) => Math.max(max, estimateEmWidth(line)), 0);
  const naturalW = maxEm * params.fontPx + PASTE_TEXT_PADDING_X_PX;
  const w = Math.min(PASTE_TEXT_MAX_W, Math.max(TEXT_DEFAULT_W, Math.ceil(naturalW)));

  const usableW = Math.max(1, w - PASTE_TEXT_PADDING_X_PX);
  // 明示改行と各行の折り返しを合算し、初期表示に必要な行数を求める。
  const rows = lines.reduce(
    (sum, line) => sum + Math.max(1, Math.ceil((estimateEmWidth(line) * params.fontPx) / usableW)),
    0
  );
  const naturalH = rows * params.fontPx * STICKY_LINE_HEIGHT_RATIO;
  const h = Math.min(
    PASTE_TEXT_MAX_H,
    Math.max(textInitialHeight(params.fontPx), Math.ceil(naturalH))
  );
  return { w, h };
}

export interface PastedTextFitResult {
  readonly text: string;
  /** 切り詰めた文字数（サロゲートペアを 1 文字として数える）。 */
  readonly droppedCount: number;
  readonly reason: TextBodyClampReason;
}

/**
 * 貼り付ける本文を、文字数上限と object.create の本文予算へ順番に収める。
 * バイト上限は domain の JSON 文字列バイト数で判定され、サロゲートペアを分断しない。
 */
export function fitPastedTextForBudget(params: {
  readonly text: string;
  readonly budgetBytes: number;
}): PastedTextFitResult {
  const lengthFit = clampTextBodyAtCaret({
    text: params.text,
    caret: params.text.length,
    maxLength: TEXT_BODY_MAX_LENGTH,
  });
  const byteFit = clampTextBodyAtCaret({
    text: lengthFit.text,
    caret: lengthFit.text.length,
    maxLength: TEXT_BODY_MAX_LENGTH,
    maxBytes: Math.max(0, params.budgetBytes),
  });
  return {
    text: byteFit.text,
    droppedCount: countCodePoints(params.text) - countCodePoints(byteFit.text),
    reason: byteFit.droppedLength > 0 ? "bytes" : lengthFit.reason,
  };
}

/** 配列化せず Unicode コードポイント数を数え、巨大な入力でも余分な配列を作らない。 */
function countCodePoints(text: string): number {
  let count = 0;
  for (const _character of text) count += 1;
  return count;
}
